/**
 * Progressive cart interactions and small commerce-page enhancements.
 * Native links/forms remain available when JavaScript is disabled.
 */
(function () {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let requestQueue = Promise.resolve();
  const noteTimers = new WeakMap();

  function setBusy(busy, key) {
    document.querySelectorAll('[data-cart-page-root], [data-cart-drawer-content]').forEach((element) => {
      element.setAttribute('aria-busy', busy ? 'true' : 'false');
    });

    if (!key) return;
    document.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.dataset.lineKey !== key) return;
      line.classList.toggle('is-updating', busy);
      line.querySelectorAll('button, input, [data-cart-remove]').forEach((control) => {
        if ('disabled' in control) control.disabled = busy;
        control.setAttribute('aria-disabled', busy ? 'true' : 'false');
      });
    });
  }

  function announce(message, isError) {
    document.querySelectorAll('[data-cart-status]').forEach((status) => {
      status.hidden = false;
      status.textContent = message;
      status.classList.toggle('is-error', Boolean(isError));
    });
  }

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  }

  function parseMarkup(markup) {
    return new DOMParser().parseFromString(markup, 'text/html');
  }

  async function refreshSections(options) {
    const settings = options || {};
    const currentPage = document.querySelector('[data-cart-page-root]');
    const pageSectionId = currentPage?.dataset.sectionId;
    const sectionIds = ['cart-drawer'];
    if (pageSectionId) sectionIds.push(pageSectionId);
    const response = await fetch(`${root}?sections=${sectionIds.join(',')}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart refresh failed');

    const sections = await response.json();
    const drawerDocument = sections['cart-drawer'] ? parseMarkup(sections['cart-drawer']) : null;
    const nextDrawerBody = drawerDocument?.querySelector('[data-cart-body]');
    if (nextDrawerBody) {
      document.querySelectorAll('[data-cart-body]').forEach((body) => {
        body.innerHTML = nextDrawerBody.innerHTML;
      });
      const count = Number(nextDrawerBody.querySelector('[data-cart-count]')?.textContent || 0);
      updateCount(count);
    }

    const pageDocument = pageSectionId && sections[pageSectionId] ? parseMarkup(sections[pageSectionId]) : null;
    const nextPage = pageDocument?.querySelector('[data-cart-page-root]');
    if (currentPage && nextPage) currentPage.replaceWith(nextPage);

    if (settings.message) announce(settings.message, settings.isError);

    if (settings.focusKey) {
      const matchingLine = Array.from(document.querySelectorAll('[data-cart-line]')).find(
        (line) => line.dataset.lineKey === settings.focusKey && line.offsetParent !== null
      );
      matchingLine?.querySelector(settings.focusTarget || '[data-cart-quantity]')?.focus({ preventScroll: true });
    }
  }

  async function cartRequest(endpoint, payload) {
    const response = await fetch(`${root}cart/${endpoint}.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    let result = {};
    try {
      result = await response.json();
    } catch (_) {
      // A useful fallback message is supplied below.
    }
    if (!response.ok) throw new Error(result.description || result.message || 'We could not update your cart.');
    return result;
  }

  function queue(task) {
    requestQueue = requestQueue.catch(() => {}).then(task);
    return requestQueue;
  }

  function updateLine(line, quantity, focusTarget) {
    const key = line.dataset.lineKey;
    if (!key) return;
    const previousQuantity = Number(line.querySelector('[data-cart-quantity]')?.defaultValue || 0);
    const safeQuantity = Math.max(0, Math.floor(Number(quantity) || 0));

    queue(async () => {
      setBusy(true, key);
      try {
        const cart = await cartRequest('change', { id: key, quantity: safeQuantity });
        await refreshSections({
          message: safeQuantity === 0 ? 'Item removed from your cart.' : 'Cart updated.',
          focusKey: safeQuantity === 0 ? null : key,
          focusTarget,
        });
        updateCount(cart.item_count || 0);
      } catch (error) {
        document.querySelectorAll('[data-cart-line]').forEach((matchingLine) => {
          if (matchingLine.dataset.lineKey !== key) return;
          const input = matchingLine.querySelector('[data-cart-quantity]');
          if (input) input.value = String(previousQuantity);
        });
        announce(error.message || 'We could not update your cart. Please try again.', true);
      } finally {
        setBusy(false, key);
      }
    });
  }

  document.addEventListener('click', (event) => {
    const decrease = event.target.closest('[data-quantity-decrease]');
    const increase = event.target.closest('[data-quantity-increase]');
    const remove = event.target.closest('[data-cart-remove]');
    const control = decrease || increase || remove;
    if (!control) return;
    const line = control.closest('[data-cart-line]');
    const input = line?.querySelector('[data-cart-quantity]');
    if (!line || !input) return;

    event.preventDefault();
    if (remove) {
      updateLine(line, 0);
      return;
    }

    const adjustment = (increase ? 1 : -1) * Number(input.step || 1);
    updateLine(line, Number(input.value) + adjustment, increase ? '[data-quantity-increase]' : '[data-quantity-decrease]');
  });

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-cart-quantity]');
    if (quantity) {
      const line = quantity.closest('[data-cart-line]');
      if (line) updateLine(line, quantity.value, '[data-cart-quantity]');
      return;
    }

    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    window.clearTimeout(noteTimers.get(note));
    noteTimers.set(
      note,
      window.setTimeout(() => {
        queue(async () => {
          try {
            await cartRequest('update', { note: note.value });
            document.querySelectorAll('[data-cart-note]').forEach((field) => {
              if (field !== note) field.value = note.value;
            });
            announce('Order note saved.');
          } catch (error) {
            announce(error.message || 'We could not save your note. Please try again.', true);
          }
        });
      },
      250
    ));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-collection-sort]')) event.target.form?.requestSubmit();
  });

  document.addEventListener('click', async (event) => {
    const copyButton = event.target.closest('[data-gift-card-copy]');
    if (!copyButton) return;
    const code = copyButton.dataset.giftCardCopy || '';
    try {
      await navigator.clipboard.writeText(code);
      copyButton.textContent = copyButton.dataset.copiedLabel || 'Copied';
    } catch (_) {
      const input = document.querySelector('[data-gift-card-code]');
      input?.select();
      document.execCommand('copy');
      copyButton.textContent = copyButton.dataset.copiedLabel || 'Copied';
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-gift-card-print]')) window.print();
  });

  document.addEventListener('lophiel:cart:refresh', () => refreshSections().catch(() => {}));

  window.LophielCommerce = { refreshCart: refreshSections };
})();
